import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  IndianRupee,
  PercentCircleIcon,
  ShieldCheck,
  Store,
  Users,
} from "lucide-react";
import { KpiCard } from "../../../Component/AdminComponents/KpiCard";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import { Badge } from "../../../Component/AdminComponents/Badge";
import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
import { useNavigate } from "react-router-dom";
import { ValueLoader } from "../../../Component/Common/ValueLoader.jsx";
import { BASE_URL } from "../../../config/api.jsx";
import axios from "axios";



export default function OverView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // NEW: State for dashboard API
  const [dash, setDash] = useState({
    category_count: 0,
    brand_count: 0,
    product_count: 0,
    company_count: 0,
  });

  // NEW: Fetch API
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}warranty/warranty-dashboard`
        );
        const json = await res.json();
        if (json.status && json.data) {
          setDash(json.data);
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const [retailerLineStats, setRetailerLineStats] = useState(null);
  const [lineLoading, setLineLoading] = useState(false);


  const authUser = JSON.parse(localStorage.getItem("authUserData"));
const locationMode = authUser?.location_mode;
const userState = authUser?.state;
const userDistrict = authUser?.district;

const fetchRetailerLineChart = async () => {
  try {
    setLineLoading(true);

    const params = {};
    if (locationMode === "state") params.state = userState;
    if (locationMode === "district") params.district = userDistrict;

    const res = await axios.get(
      `${BASE_URL}company/employee-retailer-line-chart`,
      { params }
    );

    if (res.data?.success) {
      setRetailerLineStats(res.data.data);
    }
  } catch (err) {
    console.error("Line chart API failed", err);
  } finally {
    setLineLoading(false);
  }
};

useEffect(() => {
  fetchRetailerLineChart();
}, []);

  return (
    <div className="space-y-4">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiCard
          label="Category"
          value={loading ? <ValueLoader /> : dash.category_count}
          subLabel={`Active: ${dash.category_count}`} // keeping same UI
          icon={Store}
          onClick={() => navigate("/categories")}
          className="cursor-pointer"
        />

        <KpiCard
          label="Brand"
          value={loading ? <ValueLoader /> : dash.brand_count}
          subLabel="Using platform"
          icon={CheckCircle2}
          onClick={() => navigate("/brands")}
        />

        <KpiCard
          label="Product"
          value={loading ? <ValueLoader /> : dash.product_count}
          subLabel="Field & onboarding"
          icon={Users}
          onClick={() => navigate("/products")}
          className="cursor-pointer"
        />

        <KpiCard
          label="Companies"
          value={loading ? <ValueLoader /> : dash.company_count}
          subLabel={`Total: ${dash.company_count}`}
          icon={ShieldCheck}
          onClick={() =>
            navigate("/amdcomp", { state: { showOnlyActive: true } })
          }
        />

        <KpiCard
          label="Agents"
          value={loading ? <ValueLoader /> : dash.agent_count}
          subLabel="Field & onboarding"
          icon={Users}
          onClick={() => navigate("/admagents")}
          className="cursor-pointer"   // optional 
        />

        <KpiCard
          label="Retailers"
          value={loading ? <ValueLoader /> : dash.retailer_count}
          // subLabel={`Active: ${stats.activeRetailers}`}
          icon={Store}
          onClick={() => navigate("/admretailers")}
          className="cursor-pointer"   // optional to show pointer UI
        />

        {/* <KpiCard
          label="Employees"
          // value={stats.totalRetailers}
          // subLabel={`Active: ${stats.activeRetailers}`}
          icon={Store}
          onClick={() => navigate("/allemployees")}
          className="cursor-pointer"   // optional to show pointer UI
        /> */}

        <KpiCard
          label="Price Template"
          value={loading ? <ValueLoader /> : dash.price_templates_count}
          subLabel="All Products Price Template"
          icon={PercentCircleIcon}
          onClick={() =>
            navigate("/pricetemplate")
          }
        />

        <KpiCard
          label="Connected"
          value={loading ? <ValueLoader /> : dash.connected_retailers_count}
          subLabel="Using platform"
          icon={CheckCircle2}
          onClick={() =>
            navigate("/admretailers", { state: { filterStatus: "Connected" } })
          }
        />
       <KpiCard
          label="Active Warranties"
          value={loading ? <ValueLoader /> : dash.active_warranties_count}
          // subLabel={`Total: ${stats.totalWarranties}`}
          icon={ShieldCheck}
          onClick={() =>
            navigate("/admwrnty", { state: { showOnlyActive: true } })
          }
        />

        <KpiCard
          label="Open Claims"
          value={loading ? <ValueLoader /> : dash.open_claims_count}
          // subLabel={`Settled: ${stats.settledClaims}`}
          icon={FileText}
          onClick={() =>
            navigate("/admwrnty", { state: { showOnlyOpen: true } })
          }
        />
        <KpiCard
          label="Approved Commission"
          // value={`₹ ${formatCurrency(stats.totalCommission)}`}
          // subLabel={`Pending: ₹ ${formatCurrency(stats.pendingCommission)}`}
          icon={IndianRupee}
        />
      </div>

      {/* Summary Layout (UNCHANGED UI) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
        <SectionCard
          title="Retailer Health Snapshot"
          right={
            <span className="hidden md:inline text-[11px] text-slate-400">
              Based on connection & usage
            </span>
          }
        >
          <div className="text-xs space-y-3">
            {lineLoading || !retailerLineStats ? (
              <ValueLoader />
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Connected retailers</span>
                  <span className="font-semibold text-blue-700">
                    {retailerLineStats.connected.count} /{" "}
                    {retailerLineStats.connected.total}
                  </span>
                </div>

                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-2 bg-blue-500 rounded-full"
                    style={{
                      width: `${retailerLineStats.connected.percent}%`,
                    }}
                  />
                </div>

                <p className="text-[11px] text-slate-500">
                  Retailers who have successfully connected and are actively using the
                  platform services.
                </p>
              </>
            )}
          </div>
        </SectionCard>


        <SectionCard
          title="Claims & Warranty Flow"
          right={
            <Badge color="yellow">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Monitoring
            </Badge>
          }
        >
          <div className="text-xs space-y-2">
            <p className="text-slate-600">
              Combined view of retailer portal, retailer user portal and customer
              portal flows:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-600">
              <li>
                Retailer sells warranty (from retailer dashboard), mapped to
                device IMEI & invoice.
              </li>
              <li>
                Customer or retailer raises claim (customer portal / retailer user
                portal).
              </li>
              <li>
                Ops & admin approve / reject with reasons (payment not paid, wrong
                product info / price, out of coverage etc.).
              </li>
              <li>
                Pickup / drop is arranged via channel partner, device goes to
                service centre and claim is settled.
              </li>
            </ul>
          </div>
        </SectionCard>

        <SectionCard
          title="Commission & Earnings"
          right={
            <Badge color="green">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Weekly cycle
            </Badge>
          }
        >
          <div className="text-xs space-y-2">
            <p className="text-slate-600">
              Commission generated from retailer warranty sales and onboarding by
              agents is combined and settled weekly.
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>Agent-wise earnings (onboarding + performance).</li>
              <li>Retailer wise earnings (per warranty & sales volume).</li>
              <li>
                Transfer status with bank details, transaction ID & date.
              </li>
              <li>
                Downloadable weekly/monthly statements for audit.
              </li>
            </ul>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}


// import { AlertTriangle, CheckCircle2, FileText, IndianRupee, ShieldCheck, Store, Users } from "lucide-react";
// import { KpiCard } from "../../../Component/AdminComponents/KpiCard";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { useNavigate } from "react-router-dom";

// export default function OverView({ stats }) {
//   const navigate = useNavigate();
  
//   return (
//     <div className="space-y-4">
//       {/* KPI Grid */}
//       <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//         <KpiCard
//           label="Category"
//           value={stats.totalRetailers}
//           subLabel={`Active: ${stats.activeRetailers}`}
//           icon={Store}
//           onClick={() => navigate("/categories")}
//           className="cursor-pointer"   // optional to show pointer UI
//         />

//         <KpiCard
//           label="Brand"
//           value={stats.connectedRetailers}
//           subLabel="Using platform"
//           icon={CheckCircle2}
//           onClick={() => navigate("/brands")}
//         />

//         <KpiCard
//           label="Product"
//           value={stats.totalAgents}
//           subLabel="Field & onboarding"
//           icon={Users}
//           onClick={() => navigate("/admagents")}
//           className="cursor-pointer"   // optional 
//         />
//        <KpiCard
//           label="Companies"
//           value={stats.activeWarranties}
//           subLabel={`Total: ${stats.totalWarranties}`}
//           icon={ShieldCheck}
//           onClick={() =>
//             navigate("/admwrnty", { state: { showOnlyActive: true } })
//           }
//         />
//         {/* <KpiCard
//           label="Open Claims"
//           value={stats.openClaims}
//           subLabel={`Settled: ${stats.settledClaims}`}
//           icon={FileText}
//           onClick={() =>
//             navigate("/admwrnty", { state: { showOnlyOpen: true } })
//           }
//         />
//         <KpiCard
//           label="Approved Commission"
//           value={`₹ ${formatCurrency(stats.totalCommission)}`}
//           subLabel={`Pending: ₹ ${formatCurrency(stats.pendingCommission)}`}
//           icon={IndianRupee}
//         /> */}
//       </div>

//       {/* Summary Layout */}
//       <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
//         <SectionCard
//           title="Retailer Health Snapshot"
//           right={
//             <span className="hidden md:inline text-[11px] text-slate-400">
//               Based on connection & usage
//             </span>
//           }
//         >
//           <div className="text-xs space-y-3">
//             <div className="flex justify-between items-center">
//               <span className="text-slate-600">Connected retailers</span>
//               <span className="font-semibold text-blue-700">
//                 {stats.connectedRetailers} / {stats.totalRetailers}
//               </span>
//             </div>
//             <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
//               <div
//                 className="h-2 bg-blue-500 rounded-full"
//                 style={{
//                   width: `${
//                     stats.totalRetailers
//                       ? (stats.connectedRetailers / stats.totalRetailers) * 100
//                       : 0
//                   }%`,
//                 }}
//               />
//             </div>
//             <p className="text-[11px] text-slate-500">
//               Track which retailers are activated, connected to APIs, using the app
//               for sales, warranties, and claims.
//             </p>
//           </div>
//         </SectionCard>

//         <SectionCard
//           title="Claims & Warranty Flow"
//           right={
//             <Badge color="yellow">
//               <AlertTriangle className="w-3 h-3 mr-1" />
//               Monitoring
//             </Badge>
//           }
//         >
//           <div className="text-xs space-y-2">
//             <p className="text-slate-600">
//               Combined view of retailer portal, retailer user portal and customer
//               portal flows:
//             </p>
//             <ul className="list-disc list-inside space-y-1 text-slate-600">
//               <li>
//                 Retailer sells warranty (from retailer dashboard), mapped to device
//                 IMEI & invoice.
//               </li>
//               <li>
//                 Customer or retailer raises claim (customer portal / retailer user
//                 portal).
//               </li>
//               <li>
//                 Ops & admin approve / reject with reasons (payment not paid, wrong
//                 product info / price, out of coverage etc.).
//               </li>
//               <li>
//                 Pickup / drop is arranged via channel partner, device goes to
//                 service centre and claim is settled.
//               </li>
//             </ul>
//           </div>
//         </SectionCard>

//         <SectionCard
//           title="Commission & Earnings"
//           right={
//             <Badge color="green">
//               <CheckCircle2 className="w-3 h-3 mr-1" />
//               Weekly cycle
//             </Badge>
//           }
//         >
//           <div className="text-xs space-y-2">
//             <p className="text-slate-600">
//               Commission generated from retailer warranty sales and onboarding by
//               agents is combined and settled weekly.
//             </p>
//             <ul className="list-disc list-inside text-slate-600 space-y-1">
//               <li>Agent-wise earnings (onboarding + performance).</li>
//               <li>Retailer wise earnings (per warranty & sales volume).</li>
//               <li>Transfer status with bank details, transaction ID & date.</li>
//               <li>Downloadable weekly/monthly statements for audit.</li>
//             </ul>
//           </div>
//         </SectionCard>
//       </div>
//     </div>
//   );
// }